/**
 * Campo de moneda
 * -----------------------------------------------------------------------------
 * Muestra el importe con los separadores de la moneda —`1.000.000.000,00` en
 * pesos, `1,000,000,000.00` en dólares— pero lo que se envía es siempre el valor
 * crudo `1000000000.00`: punto decimal, sin separador de miles y con los
 * decimales fijos. Así el backend recibe algo parseable sin limpiar nada.
 *
 * Uso:
 *   <div class="form-outline" data-mdb-input-init
 *        data-company-currency-init data-company-currency="COP">
 *     <input type="text" class="form-control" id="monto" name="monto" />
 *     <label class="form-label" for="monto">Monto</label>
 *   </div>
 *
 * Atributos:
 *   data-company-currency          ISO 4217 (COP por defecto)
 *   data-company-currency-locale   fuerza el formato (es-CO, en-US…)
 *   data-company-currency-decimals fuerza los decimales
 *   data-company-currency-symbol   antepone el símbolo en un input-group
 *   data-company-currency-negative permite importes negativos
 *
 * Los separadores no se codifican a mano: se leen de `Intl.NumberFormat` con
 * `formatToParts`, que ya sabe que el francés agrupa con espacio fino y que el
 * hindi agrupa de a dos cifras a partir del millar.
 */

import { syncLabel } from './floating-labels.js';

const DEFAULT_CURRENCY = 'COP';

// Moneda -> locale de su país de origen. Es el ÚLTIMO recurso: la convención de
// escritura la manda el sitio donde se usa la app, no la moneda. Un importe en
// dólares dentro de una app colombiana se escribe «10.000,50», no «10,000.50».
const CURRENCY_HOME_LOCALES = {
  COP: 'es-CO',
  USD: 'en-US',
  EUR: 'es-ES',
  MXN: 'es-MX',
  ARS: 'es-AR',
  CLP: 'es-CL',
  PEN: 'es-PE',
  BRL: 'pt-BR',
  UYU: 'es-UY',
  BOB: 'es-BO',
  PYG: 'es-PY',
  VES: 'es-VE',
  GBP: 'en-GB',
  CHF: 'de-CH',
  JPY: 'ja-JP',
};

// Atajos por clase para cambiar la convención campo a campo sin escribir el
// locale entero. `data-company-currency-locale` admite cualquier BCP-47.
const LOCALE_CLASSES = {
  'currency-co': 'es-CO', // 1.000.000,00
  'currency-es': 'es-ES', // 1.000.000,00
  'currency-ar': 'es-AR', // 1.000.000,00
  'currency-br': 'pt-BR', // 1.000.000,00
  'currency-cl': 'es-CL', // 1.000.000,00
  'currency-us': 'en-US', // 1,000,000.00
  'currency-mx': 'es-MX', // 1,000,000.00
  'currency-pe': 'es-PE', // 1,000,000.00
  'currency-gb': 'en-GB', // 1,000,000.00
  'currency-ch': 'de-CH', // 1'000'000.00
  'currency-fr': 'fr-FR', // 1 000 000,00
};

// Predeterminado de toda la app. Se fija una vez con `setCurrencyDefaults()`.
let defaultLocale = null;

/**
 * Fija la convención de escritura para todos los campos de moneda que no la
 * declaren. Alternativa a poner `lang` en el `<html>`.
 *
 * @param {{locale?: string}} options
 */
export function setCurrencyDefaults({ locale } = {}) {
  if (locale) defaultLocale = locale;
}

function localeFromClasses(wrapper) {
  const names = Object.keys(LOCALE_CLASSES);

  for (let i = 0; i < names.length; i += 1) {
    if (wrapper.classList.contains(names[i])) return LOCALE_CLASSES[names[i]];
  }

  return null;
}

/**
 * Locale con el que se formatea, de más específico a más general:
 *
 *   1. `data-company-currency-locale` del campo
 *   2. clase de atajo (`currency-co`, `currency-us`…)
 *   3. `setCurrencyDefaults({ locale })` de la app
 *   4. `lang` del `<html>`
 *   5. país de origen de la moneda
 *   6. idioma del navegador
 */
function resolveLocale(currency, wrapper) {
  const explicit = wrapper.getAttribute('data-company-currency-locale');
  if (explicit) return explicit;

  const byClass = localeFromClasses(wrapper);
  if (byClass) return byClass;

  if (defaultLocale) return defaultLocale;

  const fromDocument = typeof document !== 'undefined' && document.documentElement.lang;
  if (fromDocument) return fromDocument;

  if (CURRENCY_HOME_LOCALES[currency]) return CURRENCY_HOME_LOCALES[currency];

  return (typeof navigator !== 'undefined' && navigator.language) || 'es-CO';
}

/**
 * Separadores reales del locale, leídos de Intl en vez de adivinados.
 */
function separatorsOf(locale) {
  const parts = new Intl.NumberFormat(locale, { minimumFractionDigits: 2 }).formatToParts(11111.11);
  const group = parts.find((part) => part.type === 'group');
  const decimal = parts.find((part) => part.type === 'decimal');

  return {
    group: group ? group.value : '.',
    decimal: decimal ? decimal.value : ',',
  };
}

// Monedas sin unidad menor según ISO 4217: no admiten decimales.
const ZERO_DECIMAL = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'ISK',
  'JPY',
  'KMF',
  'KRW',
  'PYG',
  'RWF',
  'UGX',
  'UYI',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
];

/**
 * Decimales de la moneda, según las unidades menores de ISO 4217.
 *
 * No se usa `Intl.resolvedOptions().maximumFractionDigits`: ese dato es el
 * redondeo de VISUALIZACIÓN de ICU, no la precisión de la moneda. Para el peso
 * colombiano devuelve 0 —es habitual mostrarlo sin centavos— y el importe
 * guardado perdería los decimales.
 */
function decimalsOf(locale, currency, override) {
  if (override !== null && override !== '' && !Number.isNaN(Number(override))) {
    return Number(override);
  }

  return ZERO_DECIMAL.indexOf(currency) === -1 ? 2 : 0;
}

function symbolOf(locale, currency) {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbol = parts.find((part) => part.type === 'currency');
    return symbol ? symbol.value : currency;
  } catch (error) {
    return currency;
  }
}

class CurrencyInput {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('input');

    this.currency = (
      wrapper.getAttribute('data-company-currency') || DEFAULT_CURRENCY
    ).toUpperCase();
    this.locale = resolveLocale(this.currency, wrapper);
    this.decimals = decimalsOf(
      this.locale,
      this.currency,
      wrapper.getAttribute('data-company-currency-decimals')
    );
    this.allowNegative = wrapper.hasAttribute('data-company-currency-negative');
    this.separators = separatorsOf(this.locale);

    this.grouper = new Intl.NumberFormat(this.locale, {
      useGrouping: true,
      maximumFractionDigits: 0,
    });

    this._setup();
  }

  _setup() {
    const { wrapper, input } = this;

    input.setAttribute('inputmode', this.decimals > 0 ? 'decimal' : 'numeric');
    input.setAttribute('autocomplete', 'off');
    wrapper.classList.add('company-currency');

    if (input.name) {
      this.hidden = document.createElement('input');
      this.hidden.type = 'hidden';
      this.hidden.name = input.name;
      wrapper.appendChild(this.hidden);
      input.removeAttribute('name');
    }

    if (wrapper.hasAttribute('data-company-currency-symbol')) this._addSymbol();

    input.addEventListener('input', () => this._onInput());
    input.addEventListener('blur', () => this._onBlur());

    // Un valor precargado puede venir crudo («1000000.5») o ya formateado.
    if (input.value) {
      this.setValue(input.value);
    } else {
      this._sync();
    }
  }

  _addSymbol() {
    const { wrapper } = this;

    this.group = document.createElement('div');
    this.group.className = 'input-group company-currency-group';
    wrapper.parentNode.insertBefore(this.group, wrapper);

    const prefix = document.createElement('span');
    prefix.className = 'input-group-text company-currency-symbol';

    // El atributo puede traer el símbolo ya escrito; si viene vacío se deduce.
    // Deducirlo depende del locale: en `es` genérico el peso sale como «COP» y
    // solo con `es-CO` sale «$».
    const declared = wrapper.getAttribute('data-company-currency-symbol');
    prefix.textContent = declared || symbolOf(this.locale, this.currency);

    this.group.appendChild(prefix);
    this.group.appendChild(wrapper);
  }

  /**
   * Valor crudo para el backend: punto decimal, sin miles, decimales fijos.
   * Cadena vacía si el campo está vacío.
   */
  get value() {
    const parsed = this.number;
    return parsed === null ? '' : parsed.toFixed(this.decimals);
  }

  /** Valor numérico, o null si está vacío. */
  get number() {
    const { integer, fraction, negative } = this._parts(this.input.value);
    if (integer === '' && fraction === '') return null;

    const raw = `${negative ? '-' : ''}${integer || '0'}.${fraction || '0'}`;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Fija el importe. Acepta número, valor crudo («1000000.5») o ya formateado.
   */
  setValue(input) {
    if (input === null || input === '') {
      this.input.value = '';
      this._sync();
      return;
    }

    let number = input;

    if (typeof number === 'string') {
      // Si trae el separador decimal del locale, se interpreta con sus reglas;
      // si no, se asume crudo con punto.
      number = number.includes(this.separators.decimal)
        ? Number(this._parts(number).negative ? `-${this._plain(number)}` : this._plain(number))
        : Number(number.replace(/[^\d.-]/g, ''));
    }

    if (Number.isNaN(number) || number === null) {
      this.input.value = '';
      this._sync();
      return;
    }

    // El label tiene que subir aunque el valor lo escriba el componente.
    this.input.value = this._format(Math.abs(number).toFixed(this.decimals).split('.'), number < 0);
    this._sync();
  }

  /** Convierte texto del locale a notación cruda con punto. */
  _plain(text) {
    const { integer, fraction } = this._parts(text);
    return `${integer || '0'}.${fraction || '0'}`;
  }

  /**
   * Descompone lo tecleado en signo, parte entera y decimales, descartando
   * cualquier carácter que no forme parte del número.
   */
  _parts(text) {
    const value = String(text);
    const negative = this.allowNegative && value.trim().startsWith('-');

    // Se corta por el PRIMER separador decimal: los siguientes se ignoran.
    const cut = value.indexOf(this.separators.decimal);
    const head = cut === -1 ? value : value.slice(0, cut);
    const tail = cut === -1 ? '' : value.slice(cut + 1);

    const integer = head.replace(/\D/g, '');
    const fraction = tail.replace(/\D/g, '').slice(0, this.decimals);

    return { integer, fraction, negative };
  }

  _format([integer, fraction], negative) {
    const grouped = this.grouper.format(Number(integer || 0));
    const sign = negative ? '-' : '';

    if (this.decimals === 0 || fraction === undefined) return `${sign}${grouped}`;
    return `${sign}${grouped}${this.separators.decimal}${fraction}`;
  }

  _onInput() {
    const { input } = this;
    const caretFromEnd = input.value.length - (input.selectionStart || 0);

    const { integer, fraction, negative } = this._parts(input.value);
    const hasDecimalMark = input.value.includes(this.separators.decimal) && this.decimals > 0;

    if (integer === '' && fraction === '' && !hasDecimalMark) {
      input.value = negative ? '-' : '';
      this._sync();
      return;
    }

    // Mientras se escribe, los decimales se dejan tal cual estén tecleados: no
    // se rellenan a dos cifras hasta salir del campo, o «1,5» se convertiría en
    // «1,50» con el cursor en medio.
    const formatted = hasDecimalMark
      ? `${negative ? '-' : ''}${this.grouper.format(Number(integer || 0))}${
          this.separators.decimal
        }${fraction}`
      : this._format([integer], negative);

    input.value = formatted;

    const caret = Math.max(0, formatted.length - caretFromEnd);
    input.setSelectionRange(caret, caret);

    this._sync();
  }

  _onBlur() {
    if (this.input.value === '' || this.input.value === '-') {
      this.input.value = '';
      this._sync();
      return;
    }

    // Al salir sí se normaliza: decimales completos y sin separador colgando.
    this.setValue(this.value);
  }

  _sync() {
    if (this.hidden) this.hidden.value = this.value;

    // El importe lo escribe el componente al formatear, no el usuario: sin esto
    // el label se queda encima del número.
    syncLabel(this.input);

    (this.group || this.wrapper).dispatchEvent(
      new CustomEvent('valueChanged.company.currency', {
        bubbles: true,
        detail: { value: this.value, number: this.number, currency: this.currency },
      })
    );
  }

  dispose() {
    if (this.hidden) {
      this.input.name = this.hidden.name;
      this.hidden.remove();
    }

    if (this.group) {
      this.group.parentNode.insertBefore(this.wrapper, this.group);
      this.group.remove();
    }

    this.wrapper.classList.remove('company-currency');
    delete this.wrapper.__companyCurrency;
  }

  static getInstance(wrapper) {
    return wrapper.__companyCurrency || null;
  }
}

/**
 * Inicializa los campos de moneda presentes en `root`.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 */
export function setupCurrencyInputs(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-company-currency-init]').forEach((wrapper) => {
    if (wrapper.__companyCurrency) return;
    if (!wrapper.querySelector('input')) return;

    wrapper.__companyCurrency = new CurrencyInput(wrapper);
  });
}

export { CurrencyInput };
export default setupCurrencyInputs;
